import { ProjectPageModelInstance } from 'src/store/page-project/pageProject';
import { BottomSheetInstance } from 'src/store/bottom-sheet/bottomSheet';
import { PhotoDrawerInstance } from 'src/store/photo-drawer/photoDrawer';
import { I18nInstance } from 'src/store/i18n/i18n';
import { PhotoDrawerSortingOptions } from '../drawer-photo-drawer/DrawerPhotoDrawer.types';
import { useEffect, useState } from 'react';
import { DynamicModalInstance } from 'src/store/dynamic-modal/dynamicModal';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { useTypedRoute } from 'src/editor-v3/hooks/useTypedRoute';
import { useTypedNavigation } from 'src/editor-v3/hooks/useTypedNavigation';
import { STEPS } from 'src/editor-v3/components/upload-picker/UploadSteps';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';

export const useBottomSheetPhotoDrawerLogic = ({
  dynamicModal,
  bottomSheet,
  project,
  photoDrawer,
  i18n,
}: {
  dynamicModal: DynamicModalInstance;
  bottomSheet: BottomSheetInstance;
  photoDrawer: PhotoDrawerInstance;
  project: ProjectPageModelInstance;
  i18n: I18nInstance;
}) => {
  const PhotoDrawerSortingOptionsLabels = {
    [PhotoDrawerSortingOptions.SHOT_MOST_RECENT]: i18n.t('photo-drawer:shot-date-most-recent'),
    [PhotoDrawerSortingOptions.SHOT_LESS_RECENT]: i18n.t('photo-drawer:shot-date-less-recent'),
    [PhotoDrawerSortingOptions.UPLOAD_MOST_RECENT]: i18n.t('photo-drawer:upload-date-most-recent'),
    [PhotoDrawerSortingOptions.UPLOAD_LESS_RECENT]: i18n.t('photo-drawer:upload-date-less-recent'),
    [PhotoDrawerSortingOptions.NAME_ALPHABETIC]: i18n.t('photo-drawer:name-alphabetic'),
    [PhotoDrawerSortingOptions.NAME_ALPHABETIC_REVERSE]: i18n.t('photo-drawer:name-alphabetic-reverse'),
  };
  const [dropdownSelectedOption, setDropDownSelectedOption] = useState<PhotoDrawerSortingOptions>(
    PhotoDrawerSortingOptions.UPLOAD_MOST_RECENT,
  );
  const dropdownOptions = [
    PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.SHOT_MOST_RECENT],
    PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.SHOT_LESS_RECENT],
    PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.UPLOAD_MOST_RECENT],
    PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.UPLOAD_LESS_RECENT],
    PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.NAME_ALPHABETIC],
    PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.NAME_ALPHABETIC_REVERSE],
  ];

  const sendAmplitudeEvents = (option: string) => {
    amplitude.trackEvent(AnalyticsEvents.personalize.photoDrawerOrderBy, {
      order_by: option,
    });
  };

  useEffect(() => {
    if (photoDrawer.imagesUploaded) {
      setTimeout(() => {
        photoDrawer.setImagesUploaded(false);
      }, 2000);
    }
  }, [photoDrawer.imagesUploaded]);

  useEffect(() => {
    if (project.activeProject?.hasShareToken) {
      project.stopShareObserver();
      project.startShareObserver();
    }

    return () => {
      project.stopShareObserver();
    };
  }, [project.activeProject?.hasShareToken]);

  const onPressUpload = async () => {
    bottomSheet.closeBottomSheet();
    const id = await project.activeProject?.getDefaultProductIdByGroup(project.activeGroup);
    const currentParams = route.params;
    navigation.navigate(ROUTE_NAMES.Upload, {
      skipConfigurationStep: STEPS.Format,
      skipStepValue: id || project.activeGroup?.currectProductConfigurationId,
      ...currentParams,
    });
  };

  const onPressAddToProject = () => {
    project.activeGroup?.checkMinMaxProductsAndNotify();
    project.addProductsV2({
      ids: project.activeProject?.selectedThumbnails.map(t => t.originalImageId) ?? [],
      groupCode: project.activeGroup?.code,
      productConfigurationId: project.activeGroup?.productConfigurationIdDefault,
      groupId: project.activeGroup?.id,
    });
  };
  const optionCallback = (option: string) => {
    switch (option) {
      case PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.SHOT_MOST_RECENT]:
        sendAmplitudeEvents('SHOT_MOST_RECENT');
        setDropDownSelectedOption(PhotoDrawerSortingOptions.SHOT_MOST_RECENT);
        break;
      case PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.SHOT_LESS_RECENT]:
        sendAmplitudeEvents('SHOT_LESS_RECENT');
        setDropDownSelectedOption(PhotoDrawerSortingOptions.SHOT_LESS_RECENT);
        break;
      case PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.UPLOAD_MOST_RECENT]:
        sendAmplitudeEvents('UPLOAD_MOST_RECENT');
        setDropDownSelectedOption(PhotoDrawerSortingOptions.UPLOAD_MOST_RECENT);
        break;
      case PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.UPLOAD_LESS_RECENT]:
        sendAmplitudeEvents('UPLOAD_LESS_RECENT');
        setDropDownSelectedOption(PhotoDrawerSortingOptions.UPLOAD_LESS_RECENT);
        break;
      case PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.NAME_ALPHABETIC]:
        sendAmplitudeEvents('NAME_ALPHABETIC');
        setDropDownSelectedOption(PhotoDrawerSortingOptions.NAME_ALPHABETIC);
        break;
      case PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.NAME_ALPHABETIC_REVERSE]:
        sendAmplitudeEvents('NAME_ALPHABETIC_REVERSE');
        setDropDownSelectedOption(PhotoDrawerSortingOptions.NAME_ALPHABETIC_REVERSE);
        break;
    }
  };

  const onPressOption = (option: string) => {
    dynamicModal.activeModal.onPressOptionCallback(option);
  };

  const route = useTypedRoute<ROUTE_NAMES.EditorNew>();
  const navigation = useTypedNavigation<ROUTE_NAMES.EditorNew>();

  const onPressChooseButton = async () => {
    const currentParams = route.params;
    navigation.navigate(ROUTE_NAMES.Upload, {
      ...currentParams,
    });

    bottomSheet.closeBottomSheet();
  };

  return {
    dropdownOptions,
    dropdownSelectedOption,
    PhotoDrawerSortingOptionsLabels,
    setDropDownSelectedOption,
    onPressUpload,
    onPressAddToProject,
    onPressOption,
    optionCallback,
    onPressChooseButton,
  };
};
